import { FIGURE_SVG_EDITOR } from '../../utils/roles';
/**
 * variationApi — AI 문제 변형(⑤) + AI 문제 생성(⑥).
 *
 * 계약: BE-20260825-ai-variation-screens.md §2·§3·§4 · BE-20260825-problem-hub-integrated.md §7.
 * 두 기능이 같은 2단계(생성→검수→승인)·같은 검수 규약(result 불변 + result_edited 병합,
 * 보낸 키만·null=철회)이라 한 모듈에 둔다 — 화면도 카드 하나를 공유한다(생성은 원본 대조만 없음).
 */
import { fetchWithTokenRefresh, fetchAPI, AUTH_REDIRECT } from '../../utils/apiUtils';

// 401 표식은 공용 자리(utils/apiUtils)에 하나만 둔다 — 여기서는 그대로 다시 내보낸다
export { AUTH_REDIRECT };

const VAR = '/api/v2/problem-variation';
const GEN = '/api/v2/problem-generation';

// status 100 대기 / 150 처리중 / 200 완료(검수대기) / 500 실패
export const JOB_STATUS = {
  100: { label: '대기', cls: 'bg-gray-100 text-gray-700' },
  // 분석 v2 는 110(큐 대기)을 쓴다. 같은 데몬이라 여기로도 올 수 있는데 빠져 있으면
  // 폴링도 안 돌고 완료·실패 어느 분기에도 안 걸려 카드가 통째로 비어 버린다.
  110: { label: '대기(큐)', cls: 'bg-gray-100 text-gray-700' },
  150: { label: '생성 중', cls: 'bg-blue-100 text-blue-800' },
  200: { label: '검수 대기', cls: 'bg-green-100 text-green-800' },
  500: { label: '실패', cls: 'bg-red-100 text-red-800' },
};
export const REVIEW_STATUS = {
  100: { label: '검수 대기', cls: 'bg-amber-50 text-amber-700' },
  200: { label: '승인됨', cls: 'bg-green-100 text-green-800' },
  400: { label: '폐기', cls: 'bg-gray-100 text-gray-500' },
};
export const POLLING_STATUSES = [100, 110, 150];
export const JOB_POLL_MS = 6000; // 계약 5~8초 권고의 중간값(건당 수십 초)

export function getJobStatusDisplay(status) {
  const m = JOB_STATUS[status];
  return { text: m?.label || `상태 ${status}`, cls: m?.cls || 'bg-gray-100 text-gray-700' };
}
export function getReviewStatusDisplay(status) {
  const m = REVIEW_STATUS[status];
  return m ? { text: m.label, cls: m.cls } : null;
}

// 변형 강도 — 아래로 갈수록 원본에서 멀어진다(계약 §3)
export const LEVELS = [
  { value: 1, label: '수치 변경', hint: '문장·구조·개념·난이도 고정, 숫자만 바꿈' },
  { value: 2, label: '표현 재서술', hint: '구조·개념·난이도 고정, 문장 표현과 소재를 바꿈' },
  { value: 3, label: '조건 변형', hint: '개념·난이도 고정, 조건·설정·숫자를 바꿈' },
  { value: 4, label: '개념 재출제', hint: '개념만 유지, 나머지는 새로 출제' },
];

// 정답유형(=문제유형) 2축 — answerType 대분류 + choiceSubtype(5지선다 세부). 계약 §4.
// answerType 은 이제 서버가 **snake_case 3종으로 정규화해 보낸다**(계약 BE-20260826 §4 D5) —
// 검수 카드의 정확 비교가 흔들리지 않도록 산출 단계에서 맞춰 준 것이다. 그래도 이 표에 없는 값이
// 오면 getTypeLabel 이 null 을 돌려주고 배지만 빠진다(배지가 없다고 검수가 막히지는 않는다).
export const ANSWER_TYPE_LABEL = {
  multiple_choice: '5지선다',
  short_answer: '단답형',
  descriptive: '서술형',
};
export const CHOICE_SUBTYPE_LABEL = {
  correct: '정답형',
  combination: '합답형',
  negative: '부정형',
  completion: '완성형',
};
/** 검수 카드 상단 유형 배지 문구 — 예: "5지선다·합답형" */
export function getTypeLabel(problem) {
  if (!problem) return null;
  const base = ANSWER_TYPE_LABEL[problem.answerType];
  if (!base) return null;
  const sub = problem.answerType === 'multiple_choice' ? CHOICE_SUBTYPE_LABEL[problem.choiceSubtype] : null;
  return sub ? `${base}·${sub}` : base;
}

// 변형 요청의 유형 전환 옵션(§4-1). 생성(⑥)은 keep 이 없다.
export const ANSWER_TYPE_OPTIONS = [
  ['keep', '유지'],
  ['to_short', '단답형으로'],
  ['to_descriptive', '서술형으로'],
  ['to_choice', '5지선다로'],
  ['to_choice:correct', '5지선다 · 정답형'],
  ['to_choice:combination', '5지선다 · 합답형'],
  ['to_choice:negative', '5지선다 · 부정형'],
  ['to_choice:completion', '5지선다 · 완성형'],
];

/**
 * 그림 처리(§4 handoff) — 검수 시 참고 배지.
 *
 * `reject` 는 감사 수정으로 생긴 갈래다(계약 BE-20260826 §4 D4): 생성 산출의 image 블록에 도판
 * 명세(spec)가 없거나 무효라 **그리지 못했다**는 뜻. 이 표에 항목이 없으면 배지가 통째로 안 그려져,
 * 본문에 「[그림]」 글자만 남은 산출이 아무 표시 없이 검수를 통과한다 — 그래서 여기에 반드시 있어야 한다.
 */
export const IMAGE_MODE_LABEL = {
  svg: { text: 'SVG 그림', cls: 'bg-sky-50 text-sky-700', hint: '그림별 처리 결과를 확인해 주세요' },
  reuse: { text: '원본 그림 재사용', cls: 'bg-amber-50 text-amber-700', hint: '조건을 크게 바꾼 경우 그림과 어긋날 수 있습니다 — 원본과 대조해 확인하세요' },
  ...(!FIGURE_SVG_EDITOR ? { figspec: { text: '그림도 변형', cls: 'bg-sky-50 text-sky-700', hint: '도판 명세의 수치까지 함께 변형해 새로 그렸습니다' } } : {}),
  none: { text: '그림 없음', cls: 'bg-gray-100 text-gray-500', hint: '' },
  reject: {
    text: '그림 못 그림',
    cls: 'bg-red-50 text-red-700',
    hint: FIGURE_SVG_EDITOR ? '그림을 완성하지 못했습니다. 그림별 사유를 확인해 주세요.' : '도판 명세가 없거나 무효라 그림을 그리지 못했습니다 — 본문의 [그림] 자리를 지우거나 명세를 고쳐야 승인됩니다',
  },
};

function unwrap(res, fallbackMsg) {
  if (!res || res.status !== 'success') throw new Error(res?.message || fallbackMsg);
  return res.data;
}

async function requestJsonWithStatus(path, { method = 'POST', payload } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다
    // (계약 §6: soft-delete 된 사용자의 유효 토큰이 이제 401 이라 이 갈래가 실제로 돌게 됐다).
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null; // 409 의 data.reason 같은 기계 판별 코드를 화면까지 나른다
    throw err;
  }
  return body.data;
}

// ════ 변형(⑤) ════

/** 변형 요청 — 201 { variation_id, status:100 }. options: { difficulty?, answer_type? } */
export async function requestVariation(problemId, { level, options, userPrompt } = {}, getAccessToken) {
  const payload = { level };
  if (options && Object.keys(options).length > 0) payload.options = options;
  // 사용자 지시(선택) — 비면 키를 싣지 않는다. 빈 문자열과 키 없음은 서버에서 다르게
  // 취급될 수 있어 requestGeneration 과 같은 규칙을 쓴다.
  // **수용 확정** — BE-20260829-reply-fe-0828.md §4-1(9곳 전부 수용·dev 배포) · §4-2: 상한 500자
  // (공백 제거 후 기준, 초과 400) · 빈 문자열·공백만은 키를 안 보낸 것과 같다 · 프롬프트 **말미**에
  // `[추가 지시 — 요청자]` 블록으로 붙고 **옵션·규칙과 충돌하면 옵션·규칙이 이긴다** ·
  // 변형 행의 `user_prompt` 로 남아 목록·상세에 되돌아온다.
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  return requestJsonWithStatus(
    `${VAR}/problems/${problemId}/variations`,
    { method: 'POST', payload },
    getAccessToken,
    '변형 요청에 실패했습니다.',
  );
}

/** 이 문제의 변형 이력(최신순) */
export async function fetchProblemVariations(problemId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${VAR}/problems/${problemId}/variations`, {}, getAccessToken);
  const d = unwrap(res, '변형 이력을 불러오지 못했습니다.') || {};
  return Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
}

/** 변형 상세 — result(원본 산출·불변) / result_edited(수정본) / source_text(원본 평문) / image_mode */
export async function fetchVariation(variationId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${VAR}/variations/${variationId}`, {}, getAccessToken);
  return unwrap(res, '변형 상세를 불러오지 못했습니다.');
}

/**
 * 검수 수정 — 보낸 키만 result_edited 에 병합(null=철회).
 * 409 는 봉투의 `data.reason` 으로 갈래가 온다: `stale`(내가 읽은 뒤 다른 사람이 고쳤다 — 계약 §4 D6).
 * `concept_ids: null` 은 "개념 수정 철회 = 원본 승계값 복귀"이고, 정말 비우려면 `[]` 를 보낸다(회신 §2 3-1-c).
 */
export async function patchVariation(variationId, patch, getAccessToken) {
  return requestJsonWithStatus(`${VAR}/variations/${variationId}`, { method: 'PATCH', payload: patch }, getAccessToken, '수정에 실패했습니다.');
}

/**
 * 승인 — 201 { problem_id, problem_uuid }. 멱등.
 * 409 갈래: `image_placeholder` — 본문에 「[그림]」 자리표시자가 남아 있다(계약 §4 D4).
 * 검수자가 image 블록을 지우거나 도판 명세를 고쳐 PATCH 한 뒤에야 열린다.
 */
export async function approveVariation(variationId, getAccessToken) {
  return requestJsonWithStatus(`${VAR}/variations/${variationId}/approve`, { method: 'POST' }, getAccessToken, '승인에 실패했습니다.');
}
export async function rejectVariation(variationId, getAccessToken) {
  return requestJsonWithStatus(`${VAR}/variations/${variationId}/reject`, { method: 'POST' }, getAccessToken, '폐기에 실패했습니다.');
}
/** 재시도 — 실패(500)만 */
export async function retryVariation(variationId, getAccessToken) {
  return requestJsonWithStatus(`${VAR}/variations/${variationId}/retry`, { method: 'POST' }, getAccessToken, '재시도에 실패했습니다.');
}

// ════ 생성(⑥) ════

/**
 * 생성 요청 — 201 { request_uuid?, count, generation_ids[] }
 * mode: 'concept'(concept_ids 필수) | 'prompt'(user_prompt 필수). concept 모드에 지시 병행 가능.
 * options: { difficulty?(1~5 절대 목표), answer_type?(keep 없음) }
 *
 * user_prompt — 여기는 **원래부터 받던 자리**다. 2026-08-29 회신(BE-20260829-reply-fe-0828.md §4-1)에서
 * 달라진 것은 **500자 상한이 이제 생성에도 붙었다**는 것 하나다(공백 제거 후 기준, 초과 400,
 * 비문자열 400, 빈 문자열·공백만은 키를 안 보낸 것과 같다 — §4-2). 화면 자(AiPromptInput)가 같은
 * 500자를 이미 막고 있어 400 을 받을 길은 사실상 없다.
 * 붙는 자리도 여기만 예외다(§6-2) — 다른 8곳은 프롬프트 말미의 `[추가 지시 — 요청자]` 블록인데,
 * 생성은 기존 자리를 유지한다: prompt 모드는 헤더 `[출제 지시] …`(그게 본문), concept 모드는 `[추가 지시] …`.
 */
export async function requestGeneration({ mode, conceptIds, userPrompt, count = 1, options }, getAccessToken) {
  const payload = { mode, count };
  if (Array.isArray(conceptIds) && conceptIds.length > 0) payload.concept_ids = conceptIds;
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  if (options && Object.keys(options).length > 0) payload.options = options;
  return requestJsonWithStatus(`${GEN}/generate`, { method: 'POST', payload }, getAccessToken, '문제 생성 요청에 실패했습니다.');
}

/** 생성 목록 — status·review_status·mode·request_uuid 필터 */
export async function fetchGenerations(filters = {}, getAccessToken) {
  const { status, reviewStatus, mode, requestUuid, page = 1, perPage = 20 } = filters;
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (status != null) params.append('status', String(status));
  if (reviewStatus != null) params.append('review_status', String(reviewStatus));
  if (mode) params.append('mode', mode);
  if (requestUuid) params.append('request_uuid', requestUuid);
  const res = await fetchWithTokenRefresh(`${GEN}/list?${params}`, {}, getAccessToken);
  const d = unwrap(res, '생성 목록을 불러오지 못했습니다.') || {};
  const items = Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
  return { items, page: d.page ?? page, totalItems: d.total_items ?? items.length, totalPages: d.total_pages ?? 1 };
}

export async function fetchGeneration(generationId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${GEN}/generations/${generationId}`, {}, getAccessToken);
  return unwrap(res, '생성 상세를 불러오지 못했습니다.');
}
export async function patchGeneration(generationId, patch, getAccessToken) {
  return requestJsonWithStatus(`${GEN}/generations/${generationId}`, { method: 'PATCH', payload: patch }, getAccessToken, '수정에 실패했습니다.');
}
export async function approveGeneration(generationId, getAccessToken) {
  return requestJsonWithStatus(`${GEN}/generations/${generationId}/approve`, { method: 'POST' }, getAccessToken, '승인에 실패했습니다.');
}
export async function rejectGeneration(generationId, getAccessToken) {
  return requestJsonWithStatus(`${GEN}/generations/${generationId}/reject`, { method: 'POST' }, getAccessToken, '폐기에 실패했습니다.');
}
export async function retryGeneration(generationId, getAccessToken) {
  return requestJsonWithStatus(`${GEN}/generations/${generationId}/retry`, { method: 'POST' }, getAccessToken, '재시도에 실패했습니다.');
}
